/** @type {import('tailwindcss').Config} */

const plugin = require('tailwindcss/plugin');

export default {
  content: [    "./src/**/*.{js,jsx,ts,tsx}", // Adjust based on your project structure
  ],
  darkMode: 'class',
  theme: {
    extend: {
      // =======================================================================
      // ADMIN THEME COLORS - Change these values to re-theme the entire admin
      // =======================================================================
      colors: {
        // Landing (GCH) design system – slate, gold, coral (dashboard keeps these)
        'gch-bg': '#3B4F5E',
        'gch-surface': '#2E3D4A',
        'gch-accent': '#F4B942',
        'gch-cta': '#E56A54',
        'gch-cta-dark': '#E05848',
        'gch-text': '#FFFFFF',
        'gch-text-secondary': '#B8C5D0',
        'gch-text-muted': '#94A3B8',
        'gch-border': 'rgba(255, 255, 255, 0.1)',
        // Landing aliases (match the dark dashboard theme)
        'landing-bg': '#3B4F5E',
        'landing-surface': '#2E3D4A',
        'landing-text': '#FFFFFF',
        'landing-text-secondary': '#B8C5D0',
        'landing-text-muted': '#94A3B8',
        'landing-border': 'rgba(255, 255, 255, 0.1)',
        'landing-border-strong': 'rgba(255, 255, 255, 0.18)',
        'landing-accent-text': '#F4B942',
        // Primary CTA color (Arcade Gold) - for key actions like Share, Download QR
        'arcade-gold': {
          DEFAULT: '#F4B942',
          hover: '#D4A90A',
          light: 'rgba(244, 185, 66, 0.15)',
        },
        
        // Focus ring accent color (GCH gold — matches --admin-accent)
        'admin-accent': {
          DEFAULT: '#F4B942',
          light: 'rgba(244, 185, 66, 0.15)',
          ring: 'rgba(244, 185, 66, 0.4)',
        },
        
        // Complete admin semantic color system (GCH dark theme)
        'admin': {
          // Backgrounds
          'bg': '#3B4F5E',
          'surface': '#2E3D4A',

          // Text hierarchy
          'text': '#FFFFFF',
          'text-secondary': '#B8C5D0',
          'text-muted': '#94A3B8',
          'label': '#B8C5D0',

          // Borders
          'border': 'rgba(255, 255, 255, 0.1)',
          'border-hover': 'rgba(255, 255, 255, 0.18)',

          // Interactive states
          'hover': 'rgba(255, 255, 255, 0.06)',
          'active': 'rgba(255, 255, 255, 0.1)',
          'active-text': '#FFFFFF',

          // Icons
          'icon': '#B8C5D0',
          'icon-hover': '#FFFFFF',

          // Secondary action buttons
          'btn': 'rgba(255, 255, 255, 0.06)',
          'btn-hover': 'rgba(255, 255, 255, 0.1)',
          'btn-text': '#FFFFFF',
          'btn-border': 'rgba(255, 255, 255, 0.1)',

          // Badges/indicators
          'badge': 'rgba(255, 255, 255, 0.1)',
          'badge-text': '#FFFFFF',
        },
      },
      boxShadow: {
        DEFAULT: '0 1px 3px 0 rgba(0, 0, 0, 0.08), 0 1px 2px 0 rgba(0, 0, 0, 0.02)',
        md: '0 4px 6px -1px rgba(0, 0, 0, 0.08), 0 2px 4px -1px rgba(0, 0, 0, 0.02)',
        lg: '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.01)',
        xl: '0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 10px 10px -5px rgba(0, 0, 0, 0.01)',
      },
      outline: {
        blue: '2px solid rgba(0, 112, 244, 0.5)',
      },
      fontFamily: {
        inter: ['Inter', 'sans-serif'],
        arcade: ['Bungee', 'sans-serif'],
      },
      fontSize: {
        xs: ['0.75rem', { lineHeight: '1.5' }],
        sm: ['0.875rem', { lineHeight: '1.5715' }],
        base: ['1rem', { lineHeight: '1.5', letterSpacing: '-0.01em' }],
        lg: ['1.125rem', { lineHeight: '1.5', letterSpacing: '-0.01em' }],
        xl: ['1.25rem', { lineHeight: '1.5', letterSpacing: '-0.01em' }],
        '2xl': ['1.5rem', { lineHeight: '1.33', letterSpacing: '-0.01em' }],
        '3xl': ['1.88rem', { lineHeight: '1.33', letterSpacing: '-0.01em' }],
        '4xl': ['2.25rem', { lineHeight: '1.25', letterSpacing: '-0.02em' }],
        '5xl': ['3rem', { lineHeight: '1.25', letterSpacing: '-0.02em' }],
        '6xl': ['3.75rem', { lineHeight: '1.2', letterSpacing: '-0.02em' }],
      },
      screens: {
        xs: '480px',
      },
      borderWidth: {
        3: '3px',
      },
      minWidth: {
        36: '9rem',
        44: '11rem',
        56: '14rem',
        60: '15rem',
        72: '18rem',
        80: '20rem',
      },
      maxWidth: {
        '8xl': '88rem',
        '9xl': '96rem',
      },
      zIndex: {
        60: '60',
      },
      keyframes: {
        celebrate: {
          '0%, 100%': { transform: 'scale(1)' },
          '50%': { transform: 'scale(1.05)' },
        },
        fadeIn: {
          '0%': { opacity: '0' },
          '100%': { opacity: 'var(--final-opacity)' }
        },
        slideIn: {
          '0%': { 
            transform: 'scaleX(0)',
            backgroundColor: 'var(--bg-color)',
            opacity: 'var(--bg-opacity)'
          },
          '100%': { 
            transform: 'scaleX(1)',
            backgroundColor: 'var(--bg-color)',
            opacity: 'var(--bg-opacity)'
          }
        },
        modalEnter: {
          '0%': {
            opacity: '0',
            transform: 'translate(0, 24px) scale(0.95)'
          },
          '100%': {
            opacity: '1',
            transform: 'translate(0, 0) scale(1)'
          }
        },
        revealContent: {
          '0%': { opacity: '0', transform: 'translateY(-4px)' },
          '100%': { opacity: '1', transform: 'translateY(0)' }
        },
        shimmer: {
          '0%': { transform: 'translateX(-100%)' },
          '100%': { transform: 'translateX(100%)' }
        }
      },
      animation: {
        celebrate: 'celebrate 1s ease-in-out 2',
        fadeIn: 'fadeIn 0.8s cubic-bezier(0.165, 0.84, 0.44, 1) forwards',
        slideIn: 'slideIn 0.8s cubic-bezier(0.165, 0.84, 0.44, 1) forwards',
        'modal-enter': 'modalEnter 0.3s cubic-bezier(0.21, 1.02, 0.73, 1) forwards',
        'reveal-content': 'revealContent 0.25s ease-out forwards',
        shimmer: 'shimmer 1.5s ease-in-out infinite'
      }
    },
  },
  plugins: [
    // eslint-disable-next-line global-require
    require('@tailwindcss/forms'),
    // add custom variant for expanding sidebar
    plugin(({ addVariant, e }) => {
      addVariant('sidebar-expanded', ({ modifySelectors, separator }) => {
        modifySelectors(({ className }) => `.sidebar-expanded .${e(`sidebar-expanded${separator}${className}`)}`);
      });
    }),

  ],
};
